import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import { Lock as LockIcon, ContactSupport as ContactIcon } from '@mui/icons-material';
import useFeatureToggle from '../../hooks/useFeatureToggle';

/**
 * FeatureGuard component that conditionally renders children based on feature status
 * Similar to ProtectedRoute but for feature toggles
 * Automatically extracts partnerId or tripId from URL params if not provided
 */
const FeatureGuard = ({ 
  featureName, 
  partnerId = null,
  tripId = null,
  children, 
  fallback = null, 
  showLoading = true,
  customMessage = null,
  showContactSupport = true
}) => {
  const urlParams = useParams();
  
  // Use provided partnerId, or try to extract from URL params
  // Common parameter names: propertyId, partnerId, id
  const effectivePartnerId = partnerId || 
    parseInt(urlParams.propertyId) || 
    parseInt(urlParams.partnerId) || 
    parseInt(urlParams.id) || 
    null;

  // Use provided tripId, or try to extract from URL params
  const effectiveTripId = tripId || 
    parseInt(urlParams.tripId) || 
    null;

  const { isEnabled, loading, error, isAdmin } = useFeatureToggle(
    featureName, 
    effectivePartnerId, 
    effectiveTripId
  );

  // Loading state
  if (loading && showLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        p: 4
      }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1" color="textSecondary">
          Checking feature access...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error checking feature access: {error}
        </Alert>
        <Typography variant="body2" color="textSecondary">
          Please refresh the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  // Feature is enabled - show the protected content
  if (isEnabled) {
    return children;
  }

  // Feature is not enabled - show fallback or default message
  if (fallback) {
    return fallback;
  }

  // Default fallback UI
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '400px',
      p: 3
    }}>
      <Paper 
        elevation={0}
        sx={{ 
          textAlign: 'center', 
          p: 4,
          maxWidth: 500,
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2
        }}
      >
        <LockIcon sx={{ 
          fontSize: 64, 
          color: 'grey.400', 
          mb: 2 
        }} />
        
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Feature Not Available
        </Typography>
        
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {customMessage || `The "${featureName}" feature is not enabled for your account.`}
        </Typography>
        
        {showContactSupport && (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Please contact WorldExplore support to upgrade your plan and gain access to this feature.
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<ContactIcon />}
              href="mailto:support@worldexplore.net"
              sx={{ mr: 1 }}
            >
              Contact Support
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FeatureGuard;
